import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatRut, formatMoneyWithCurrency } from '../../../utils/formatter';
import { documentsDic } from '../../../utils/dataDictionary';
import { CurrencyIcon } from 'src/utils/currencyIcon';
import moment from 'moment';
import FilterTabs from '../../FilterTabs/FilterTabs';
import MatchesTab from './MatchesTab';
import NominasTab from './NominasTab';
import CommentsTab from './CommentsTab';
import ParametrizacionTab from './ParametrizacionTab';
import ExportActions from '../../export/ExportActions';
import './TaxDocumentDetail.css';
import { banksFormat } from 'src/utils/bankList';

const CreditsDetail = ({ data }) => {
  const { t } = useTranslation('documents');
  const [activeTab, setActiveTab] = useState('detalles');
  const isReceived = data.meta.isReceived;
  const isCreditOrDebitNote = [56, 61, 111, 112, "56", "61", "111", "112"].includes(data.meta.documentCode);

  const commentCount = data.meta?.innerComments?.length || 0;
  const tabs = useMemo(() => ([
    { key: 'detalles', label: t('creditDetail.tabs.details') },
    { key: 'matches', label: t('creditDetail.tabs.matches') },
    { key: 'parametrizacion', label: t('creditDetail.tabs.parametrization') },
    {
      key: 'comentarios',
      label: commentCount > 0
        ? t('creditDetail.tabs.commentsCount', { count: commentCount })
        : t('creditDetail.tabs.comments'),
    },
  ]), [t, commentCount]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'detalles':
        return (
          <div className="document-content">
            {/* Document Stamp */}
            <div className="document-stamp">

              <div className="document-stamp-number">
                {data.meta?.paymentNumber && data.meta?.totalPayments ? 
                  t('creditDetail.installmentOf', { current: data.meta.paymentNumber, total: data.meta.totalPayments }) : 
                  (data.meta.documentId || data.meta.number) ? `Nº ${data.meta.documentId || data.meta.number}` : t('creditDetail.noNumber')
                }
              </div>
              <div className="document-stamp-status">
                <span className={`status-badge ${
                  data.meta.isPaid 
                    ? 'paid' 
                    : data.meta?.isValid === false 
                      ? 'invalid' 
                      : 'pending'
                }`} style={{
                  ...(data.meta?.isValid === false && {
                    background: 'linear-gradient(135deg, #fef3f3 0%, #fde8e8 100%)',
                    border: '1px solid #f5c6cb',
                    color: '#991b1b'
                  })
                }}>
                  {data.meta?.isValid === false 
                    ? t('creditDetail.status.voided')
                    : data.meta.isPaid 
                      ? t('creditDetail.status.paid')
                      : t('creditDetail.status.pending')
                  }
                </span>
              </div>
            </div>
            
            {/* Main Info Grid */}
            <div className="document-grid">
              <div>
                <div className="info-block">
                  <div className="info-label">{t('creditDetail.creditType')}</div>
                  <div className="info-value" style={{ fontWeight: 600, fontSize: '1.2em' }}>
                    {data.meta?.creditType || documentsDic[parseInt(data.meta.documentCode)]?.fullName || data.meta.summary || t('creditDetail.noType')}
                  </div>
                </div>
                <div className="info-block">
                  <div className="info-label">{t('creditDetail.identifier')}</div>
                  <div className="counterpart-name" style={{ fontWeight: 800, fontSize: '1.1em' }}>
                    {banksFormat[data.name] || data.name ? `${banksFormat[data.name] || data.name}: ` : ''}
                    {data.meta?.identificator && `${data.meta.identificator}`}
                  </div>
                  <div className="info-value">
                    <span className="secondary" style={{ fontWeight: 600 }}>
                      {formatRut(isReceived ? data.meta.issuerId : data.meta.receiverId) || ""}
                    </span>
                  </div>
                </div>
                <div className="info-row">
                  <div className="info-block">
                    <div className="info-label">{t('creditDetail.issueDate')}</div>
                    <div className="info-value">
                      {moment(data.documentDate).format("DD/MM/YYYY")}
                    </div>
                  </div>
                  <div className="info-block">
                    <div className="info-label">{t('creditDetail.dueDate')}</div>
                    <div className="info-value">
                      {data.originalDueDate ? moment(data.originalDueDate).format("DD/MM/YYYY") : t('creditDetail.noInfo')}
                    </div>
                  </div>
                  <div className="info-block">
                    <div className="info-label">{t('creditDetail.paymentDate')}</div>
                    <div className="info-value">
                      {data.paymentDate ? moment(data.paymentDate.substr(0, 10)).format("DD/MM/YYYY") : t('creditDetail.noInfo')}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Totals Card - Credit Specific */}
            <div className="totals-card">
              <div className="totals-segment">
                <div className="totals-label">{t('creditDetail.capital')}</div>
                <div className="totals-value">
                  {formatMoneyWithCurrency(
                    data.meta?.stock || 0, 
                    data.currencyType
                  )}
                </div>
              </div>
              <div className="totals-segment">
                <div className="totals-label">{t('creditDetail.interest')}</div>
                <div className="totals-value">
                  {formatMoneyWithCurrency(
                    data.meta?.interest || 0, 
                    data.currencyType
                  )}
                </div>
              </div>
              <div className="totals-segment">
                <div className="totals-label">{t('creditDetail.installment')}</div>
                <div className="totals-value">
                  {formatMoneyWithCurrency(
                    (() => {
                      // Calcular cuota: priorizar quota/payment, luego suma de capital + interés
                      const capital = data.meta?.stock || 0;
                      const interest = data.meta?.interest || 0;
                      const cuotaCalculada = capital + interest;
                      
                      return data.meta?.quota || data.meta?.payment || cuotaCalculada || 0;
                    })(), 
                    data.currencyType
                  )}
                </div>
              </div>
            </div>

            {/* Export Actions */}
            <ExportActions
              documentData={data}
              exportType="rendiciones"
              pdfUrl={data.meta.download?.pdf?.url || data.meta.download?.html?.url}
              xmlUrl={data.meta.download?.xml?.url}
              showPdf={!!(data.meta.download?.pdf?.url || data.meta.download?.html?.url)}
              showXml={!!data.meta.download?.xml?.url}
              showExcel={true}
            />

            {/* Glosa Section */}
            <div className="glosa-section">
              <div className="info-label">{t('creditDetail.glosa')}</div>
              {(() => {
                const filteredGlosa = data.meta?.detail?.filter(item => {
                  // Filter out rows where all relevant fields are null/empty
                  const hasDescription = item.description || item.name || item.item;
                  const hasUnitPrice = item.unit_price && item.unit_price !== 0;
                  const hasQuantity = item.quantity && item.quantity !== 0;
                  const hasTotalPrice = item.total_price && item.total_price !== 0;
                  
                  return hasDescription || hasUnitPrice || hasQuantity || hasTotalPrice;
                }) || [];

                return filteredGlosa.length > 0 ? (
                  <div className="glosa-table">
                    <div className="glosa-items">
                      {filteredGlosa.map((item, index) => (
                        <div key={index} className="glosa-item">
                          <div className="glosa-item-description">
                            {item.name?.trim() && item.description?.trim() ? (
                              <>
                                <div>{item.name.trim()}</div>
                                <div className="glosa-item-subdescription">{item.description.trim()}</div>
                              </>
                            ) : (
                              item.name?.trim() || item.description?.trim() || item.item || t('common.noDescription')
                            )}
                          </div>
                          <div className="glosa-item-metrics">
                            <div className="glosa-metric">
                              <div className="glosa-metric-label">TOTAL</div>
                              <div className="glosa-metric-value">
                                {formatMoneyWithCurrency(
                                  item.total_price || (item.unit_price * (item.quantity || 1)) || 0, 
                                  data.currencyType,
                                  true,
                                  true
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="glosa-empty">
                    {t('creditDetail.noGlosa')}
                  </div>
                );
              })()}
            </div>
          </div>
        );
      case 'matches':
        return (
          <MatchesTab data={data} />
        );
      case 'nominas':
        return (
          <NominasTab data={data} />
        );
      case 'parametrizacion':
        return (
          <ParametrizacionTab data={data} />
        );
      case 'comentarios':
        return (
          <CommentsTab data={data} />
        );
      default:
        return null;
    }
  };

  return (
    <div className="document-detail-container">
      {/* Tabs */}
      <FilterTabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        className="document-filter-tabs"
      />

      {/* Tab Content */}
      <div className="tab-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default CreditsDetail; 